/// <reference types="w3c-web-serial" />
/// <reference types="w3c-web-usb" />

/**
 * A serial port over WebUSB, in the shape of a Web Serial `SerialPort`, so
 * that esptool-js and the serial board run on it unchanged. The page's README
 * describes the bridges it drives and why.
 */

/** The USB devices a bridge driver exists for, as `navigator.usb.requestDevice` filters. */
export const USB_SERIAL_FILTERS: USBDeviceFilter[] = [
    // Espressif USB-Serial/JTAG
    { vendorId: 0x303a, productId: 0x1001 },
    // Silicon Labs CP210x
    { vendorId: 0x10c4, productId: 0xea60 },
];

/** How one kind of USB-to-UART bridge is set up and how it drives the modem lines. */
interface Bridge {
    /** The interface with the bulk endpoints. */
    readonly data: number;
    /** Claim what the bridge needs on an open device. */
    claim(device: USBDevice): Promise<void>;
    /** Start the UART at `baudRate`. */
    start(device: USBDevice, baudRate: number): Promise<void>;
    stop(device: USBDevice): Promise<void>;
    setLines(device: USBDevice, dtr: boolean, rts: boolean): Promise<void>;
}

/**
 * USB CDC-ACM, as Espressif's USB-Serial/JTAG presents it. The UART rate is
 * not a thing on it, so only the modem lines are set. The control interface
 * (0) is claimed first: Chrome for Android detaches the kernel's CDC driver
 * only through it. macOS keeps its driver on it, and the chip then takes
 * SET_CONTROL_LINE_STATE addressed to the data interface.
 */
class CdcAcm implements Bridge {
    readonly data = 1;
    #control = 1;

    async claim(device: USBDevice): Promise<void> {
        try {
            await device.claimInterface(0);
            this.#control = 0;
        } catch {
            this.#control = this.data;
        }
        await claim(device, this.data);
    }

    async start(): Promise<void> {}

    async stop(): Promise<void> {}

    async setLines(device: USBDevice, dtr: boolean, rts: boolean): Promise<void> {
        const value = (dtr ? 1 : 0) | (rts ? 2 : 0);
        const setup = { request: SET_CONTROL_LINE_STATE, value, index: this.#control };
        await control(device, { requestType: "class", recipient: "interface", ...setup });
    }
}

/** Silicon Labs CP210x, driven with its vendor requests (Silicon Labs AN571). */
class Cp210x implements Bridge {
    readonly data = 0;

    async claim(device: USBDevice): Promise<void> {
        await claim(device, this.data);
    }

    async start(device: USBDevice, baudRate: number): Promise<void> {
        await this.#request(device, IFC_ENABLE, 1);
        // 8 data bits, no parity, 1 stop bit
        await this.#request(device, SET_LINE_CTL, 0x0800);
        const rate = new Uint8Array(4);
        new DataView(rate.buffer).setUint32(0, baudRate, true);
        await this.#request(device, SET_BAUDRATE, 0, rate);
    }

    async stop(device: USBDevice): Promise<void> {
        await this.#request(device, IFC_ENABLE, 0);
    }

    async setLines(device: USBDevice, dtr: boolean, rts: boolean): Promise<void> {
        // The high byte says which lines the low byte sets.
        await this.#request(device, SET_MHS, 0x0300 | (dtr ? 1 : 0) | (rts ? 2 : 0));
    }

    #request(device: USBDevice, request: number, value: number, data?: Uint8Array): Promise<void> {
        const setup = { request, value, index: this.data };
        return control(device, { requestType: "vendor", recipient: "interface", ...setup }, data);
    }
}

const SET_CONTROL_LINE_STATE = 0x22;

const IFC_ENABLE = 0x00;
const SET_LINE_CTL = 0x03;
const SET_MHS = 0x07;
const SET_BAUDRATE = 0x1e;

async function control(device: USBDevice, setup: USBControlTransferParameters, data?: Uint8Array): Promise<void> {
    const result = await device.controlTransferOut(setup, data as BufferSource | undefined);
    if (result.status !== "ok") {
        throw new Error(`USB control request 0x${setup.request.toString(16)}: ${result.status}`);
    }
}

/** Claim an interface the port cannot work without; one an OS driver holds is refused with what to do instead. */
async function claim(device: USBDevice, number: number): Promise<void> {
    try {
        await device.claimInterface(number);
    } catch (e) {
        if (!(e instanceof DOMException && e.name === "NetworkError")) throw e;
        throw new Error(
            `The operating system's driver holds interface ${number} of this USB device, so WebUSB cannot use it.` +
                " Use the Board (WebSerial) option instead.",
        );
    }
}

function bridgeFor(device: USBDevice): Bridge {
    if (device.vendorId === 0x10c4) return new Cp210x();
    return new CdcAcm();
}

/** The largest read asked of the bulk IN endpoint; a bridge ends a read early on a short packet. */
const READ_SIZE = 16384;

/**
 * The port. `disconnect` fires when the device leaves USB, which the
 * connector learns from `navigator.usb` and passes on with `unplugged`.
 */
export class UsbSerialPort extends EventTarget {
    readonly device: USBDevice;
    readable: ReadableStream<Uint8Array> | null = null;
    writable: WritableStream<Uint8Array> | null = null;
    readonly #bridge: Bridge;
    #dtr = false;
    #rts = false;

    constructor(device: USBDevice) {
        super();
        this.device = device;
        this.#bridge = bridgeFor(device);
    }

    getInfo(): SerialPortInfo {
        return { usbVendorId: this.device.vendorId, usbProductId: this.device.productId };
    }

    async open(options: SerialOptions): Promise<void> {
        if (this.readable !== null) throw new DOMException("The port is already open.", "InvalidStateError");
        const device = this.device;
        if (!device.opened) await device.open();
        if (device.configuration === null) await device.selectConfiguration(1);
        await this.#bridge.claim(device);
        await this.#bridge.start(device, options.baudRate);
        const endpoints = device.configuration!.interfaces.find((each) => each.interfaceNumber === this.#bridge.data)!
            .alternate.endpoints;
        const input = endpoints.find((each) => each.direction === "in" && each.type === "bulk");
        const output = endpoints.find((each) => each.direction === "out" && each.type === "bulk");
        if (input === undefined || output === undefined) throw new Error("The USB device has no bulk endpoints.");
        this.readable = readableOf(device, input.endpointNumber);
        this.writable = writableOf(device, output.endpointNumber);
    }

    /** Stop the UART and let go of the device, which ends a read in progress. */
    async close(): Promise<void> {
        const device = this.device;
        this.readable = null;
        this.writable = null;
        if (!device.opened) return;
        try {
            await this.#bridge.stop(device);
        } catch {
            // The device may be gone; closing it is what matters.
        }
        await device.close();
    }

    async setSignals(signals: SerialOutputSignals): Promise<void> {
        this.#dtr = signals.dataTerminalReady ?? this.#dtr;
        this.#rts = signals.requestToSend ?? this.#rts;
        await this.#bridge.setLines(this.device, this.#dtr, this.#rts);
    }

    /** The device left USB. */
    unplugged(): void {
        this.readable = null;
        this.writable = null;
        this.dispatchEvent(new Event("disconnect"));
    }
}

function readableOf(device: USBDevice, endpoint: number): ReadableStream<Uint8Array> {
    return new ReadableStream<Uint8Array>({
        // Loops past empty packets: a pull that enqueues nothing is not called again.
        async pull(controller) {
            for (;;) {
                const result = await device.transferIn(endpoint, READ_SIZE);
                if (result.status === "stall") await device.clearHalt("in", endpoint);
                const data = result.data;
                if (data !== undefined && data.byteLength > 0) {
                    controller.enqueue(new Uint8Array(data.buffer, data.byteOffset, data.byteLength).slice());
                    return;
                }
            }
        },
    });
}

function writableOf(device: USBDevice, endpoint: number): WritableStream<Uint8Array> {
    return new WritableStream<Uint8Array>({
        async write(chunk) {
            const result = await device.transferOut(endpoint, chunk as BufferSource);
            if (result.status !== "ok") throw new Error(`USB write: ${result.status}`);
        },
    });
}
